import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDarkMode } from '../../context/DarkModeContext';
import UnifiedBookCard from '../../components/Books/UnifiedBookCard';
import { SeriesStatusBadge } from '../../components/Books/SeriesStatusBadge';
import { authorBooks } from './data';
export const BooksTab = ({
  books
}: {
  books: typeof authorBooks;
}) => {
  const [viewFilter, setViewFilter] = useState<'all' | 'standalone' | 'series'>('all');
  const {
    darkMode
  } = useDarkMode();
  const allBooks = useMemo(() => {
    const seriesBooks = books.series.flatMap(s => s.books.map(b => ({
      ...b,
      seriesName: s.name,
      totalSeriesBooks: s.books.length,
      seriesStatus: 'ongoing' as const
    })));
    return [...books.standalone, ...seriesBooks];
  }, [books]);
  return <div className="space-y-6" data-id="element-6409" data-mp="2j60l1mjriwvh">
      {/* Filter Bar */}
      <div className="flex items-center gap-4 mb-6" data-id="element-6410" data-mp="2o64wuz9q73fd">
        <div className={`inline-flex rounded-lg p-1 ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`} data-id="element-6411" data-mp="zye0nz487e2">
          {(['all', 'standalone', 'series'] as const).map(filter => <button key={filter} onClick={() => setViewFilter(filter)} className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${viewFilter === filter ? 'bg-indigo-600 text-white shadow-sm' : darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-6412" data-mp="3s4evb94lbm2w">
              {filter.charAt(0).toUpperCase() + filter.slice(1)}
            </button>)}
        </div>
      </div>

      {/* Content */}
      <AnimatePresence mode="wait" data-id="element-6413" data-mp="3n4tmm6zmbzcz">
        <motion.div key={viewFilter} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.2
      }} data-id="element-6414" data-mp="3j51jwawwonfj">
          {viewFilter === 'all' && <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6" data-id="element-6415" data-mp="39ghbrh98gh">
              {allBooks.map(book => <div key={book.id} className="w-full" data-id="element-6416" data-mp="534496fyhhxb">
                  <UnifiedBookCard book={{
              ...book,
              spiceLevel: book.heatLevel,
              seriesName: (book as any).seriesName,
              seriesNumber: (book as any).seriesPosition,
              totalSeriesBooks: (book as any).totalSeriesBooks,
              seriesStatus: (book as any).seriesStatus,
              darknessLevel: book.darknessLevel,
              endingType: (book as any).endingType,
              relationshipType: book.characterRelationships?.[0]?.type,
              isNew: (book as any).isNew
            }} data-id="element-6417" data-mp="93xuw7ks9atw" />
                </div>)}
            </div>}

          {viewFilter === 'standalone' && <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6" data-id="element-6418" data-mp="2hbb49weza21p">
              {books.standalone.map(book => <div key={book.id} className="w-full" data-id="element-6419" data-mp="2d8eg7q6o5dqd">
                  <UnifiedBookCard book={{
              ...book,
              spiceLevel: book.heatLevel,
              darknessLevel: book.darknessLevel,
              endingType: book.endingType,
              relationshipType: book.characterRelationships?.[0]?.type,
              isNew: book.isNew
            }} data-id="element-6420" data-mp="28bxokm09gh8j" />
                </div>)}
            </div>}

          {viewFilter === 'series' && <div className="space-y-12" data-id="element-6421" data-mp="1kk8vh3fm7oqj">
              {books.series.map((series, idx) => <div key={idx} className={`rounded-2xl ${darkMode ? 'bg-gray-800/50' : 'bg-white border border-gray-100 shadow-sm'}`} data-id="element-6422" data-mp="1fns2nvxh0m74" data-mpi={idx}>
                  <div className="px-6 pt-6 pb-4" data-id="element-6423" data-mp="3kmrvgenlhyf6">
                    <div className="flex items-center gap-3 mb-2" data-id="element-6424" data-mp="3kpmhubq65tb8">
                      <h3 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`} data-id="element-6425" data-mp="35yds82m9o7y3">
                        {series.name}
                      </h3>
                      <SeriesStatusBadge status={(series as any).status || 'ongoing'} completedDate={(series as any).completedDate} size="md" data-id="element-6426" data-mp="2x0bo3e78kz6l" />
                    </div>
                    <p className={`max-w-3xl ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-6427" data-mp="2s076fkuldyes">
                      {series.description}
                    </p>
                  </div>
                  <div className="px-6 pb-6" data-id="element-6428" data-mp="q6znm1frsyo5">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6" data-id="element-6429" data-mp="2apa940oqn0t0">
                      {series.books.map(book => <div key={book.id} className="w-full" data-id="element-6430" data-mp="25omaoty6jhor">
                          <UnifiedBookCard book={{
                    ...book,
                    spiceLevel: book.heatLevel,
                    darknessLevel: book.darknessLevel,
                    endingType: book.endingType,
                    relationshipType: book.characterRelationships?.[0]?.type,
                    seriesName: series.name,
                    seriesNumber: (book as any).seriesPosition,
                    totalSeriesBooks: series.books.length,
                    seriesStatus: (series as any).status || 'ongoing'
                  }} data-id="element-6431" data-mp="2kgelqtqxy9ko" />
                        </div>)}
                    </div>
                  </div>
                </div>)}
            </div>}
        </motion.div>
      </AnimatePresence>
    </div>;
};